/**
 * SSE Client Wrapper — sse_v1
 * Auto-reconnect with exponential backoff
 *
 * sse_pool_v1: 1 EventSource ต่อ 1 token ต่อ 1 แท็บ — ทุก component แชร์เส้นเดียวกัน
 *   เดิมแต่ละ component เปิด EventSource ของตัวเอง (Layout + StaffChatWidget + Inbox/QuotaDashboard)
 *   = 3 connection ค้างต่อ 1 คน, เซิร์ฟเวอร์ push event เดียวกันซ้ำ 3 รอบ,
 *   และตอน token ตายก็ reconnect วนพร้อมกัน 3 เส้น
 *   ตอนนี้ connectSSE() แค่ "สมัครสมาชิก" เข้า pool ที่มีอยู่ ถ้ายังไม่มีถึงจะเปิดใหม่
 *   และ pool จะปิดตัวเองเมื่อ subscriber คนสุดท้าย close()
 *
 * Usage: (เหมือนเดิมทุกอย่าง — ผู้เรียกไม่ต้องรู้ว่ามี pool)
 *   import { connectSSE } from '../lib/sse';
 *   const conn = connectSSE({
 *     onConversationUpdated: (data) => { ... }
 *   });
 *   // cleanup:
 *   conn.close();
 */

const SSE_URL = '/api/events';
const MAX_RECONNECT_DELAY = 5000; /* sse_fast_reconnect_v1: was 30000 — cap at 5s so chat recovers fast after drop/reload */
const INITIAL_RECONNECT_DELAY = 1000;

/* sse_auth_stop_v1: EventSource ไม่บอก status code ตอน error — จาก onerror อย่างเดียว
   แยกไม่ออกว่า "เน็ตสะดุด" (ต้อง retry) หรือ "token ใช้ไม่ได้แล้ว" (retry ไปก็เท่านั้น)
   ผลคือ token ตายเมื่อไหร่ แท็บที่เปิดค้างจะยิง /api/events ทุก 5 วิไปเรื่อยๆ ไม่มีวันหยุด

   วิธีแก้: พลาดติดกันหลายครั้งเมื่อไหร่ ให้ยิง fetch ไปที่ endpoint เดิมด้วย token เดิม
   เพื่อ "ถาม status code ตรงๆ" — 401/403 = token ตายจริง หยุดถาวรแล้วเด้งไป login
   ใช้ endpoint เดียวกัน + token เดียวกัน จึงไม่มีปัญหา false positive จาก
   endpoint อื่นที่สิทธิ์ไม่ตรงกัน (สำคัญ เพราะ bill app ใช้ bill_token คนละตัว) */
const LONG_MAX_RECONNECT_DELAY = 30000;  // พลาดติดกันเยอะแล้วค่อยถ่างออก
const SLOW_DOWN_AFTER_FAILURES = 5;
const AUTH_PROBE_AFTER_FAILURES = 3;
const AUTH_PROBE_MIN_INTERVAL = 60000;   // อย่าถามถี่เกิน
const AUTH_PROBE_TIMEOUT = 8000;

/* sse_pool_v1: ชื่อ event ฝั่งเซิร์ฟเวอร์ -> ชื่อ handler ที่ผู้เรียกส่งเข้ามา
   เพิ่ม event ใหม่ = เพิ่มบรรทัดเดียวตรงนี้ */
const EVENT_HANDLERS = {
  conversation_updated: 'onConversationUpdated',
  new_message: 'onNewMessage',                    // realtime_v1: push message ตัวจริง — ไม่ต้อง refetch
  quota_alert: 'onQuotaAlert',                    // quota_v1
  slip_verified: 'onSlipVerified',                // slip_v1: ผลตรวจสลิป (auto mode)
  slip_quota_full: 'onSlipQuotaFull',             // slip_quota_v1: quota เต็ม 100%
  slip_uploaded: 'onSlipUploaded',                // slip_upload_sse_v1: ลูกค้าอัพสลิป → หน้า bill เด้ง
  /* staff_chat_v1: ย้ายมาจาก EventSource ที่ StaffChatWidget เคยเปิดเอง */
  staff_message: 'onStaffMessage',
  staff_conversation_new: 'onStaffConversationNew',
  staff_read: 'onStaffRead',                      // staff_chat_read_v3
};

/* sse_pool_v1: token -> pool. แยกตาม token เพราะ bill app ใช้ bill_token คนละตัวกับฝั่งแชท */
const pools = new Map();

/* poll_adaptive_v1: ให้ที่อื่นรู้ว่า SSE ยังต่ออยู่ไหม จะได้ลด/เพิ่มความถี่ polling ตามสถานการณ์
   (ต่ออยู่ = event มาเองแบบ realtime, poll เป็นแค่ตาข่ายกันพลาด → ถี่น้อยลงได้) */
const statusListeners = new Set();

function notifyStatus(token, connected) {
  Array.from(statusListeners).forEach((fn) => {
    try { fn(connected, token); } catch (e) { console.error('[SSE] status listener error:', e); }
  });
}

export function isSSEConnected(token) {
  const t = token || localStorage.getItem('token');
  const pool = t ? pools.get(t) : null;
  return !!(pool && pool.connected);
}

/* คืนฟังก์ชันสำหรับ unsubscribe */
export function onSSEStatus(fn) {
  statusListeners.add(fn);
  return () => statusListeners.delete(fn);
}

function createPool(token) {
  const pool = {
    token,
    eventSource: null,
    subscribers: new Set(),
    reconnectDelay: INITIAL_RECONNECT_DELAY,
    reconnectTimer: null,
    destroyed: false,
    connected: false, /* poll_adaptive_v1 */
    consecutiveFailures: 0, /* sse_auth_stop_v1: นับเฉพาะที่พลาดติดกันโดยไม่เคยต่อติดเลย */
    authFailed: false,
    lastProbeAt: 0,
    ticketUnsupported: false, /* sse_ticket_v1: backend ยังไม่มี endpoint ออกตั๋ว */
  };

  /* กระจาย event ให้ทุก subscriber — parse JSON ครั้งเดียว ไม่ใช่ครั้งละ subscriber
     snapshot ก่อนวน เผื่อ subscriber ปิดตัวเองระหว่างอยู่ใน callback
     และ try/catch ทีละตัว เพื่อไม่ให้ subscriber ที่ throw ทำให้ตัวอื่นไม่ได้รับ event */
  const emit = (key, arg) => {
    Array.from(pool.subscribers).forEach((h) => {
      const fn = h[key];
      if (typeof fn !== 'function') return;
      try { fn(arg); } catch (e) { console.error(`[SSE] ${key} handler error:`, e); }
    });
  };

  /* sse_auth_stop_v1: token ตายแล้ว — เลิก retry ถาวร แล้วบอกให้แอปพาไป login */
  const stopForAuthFailure = () => {
    if (pool.authFailed) return;
    pool.authFailed = true;
    if (pool.reconnectTimer) {
      clearTimeout(pool.reconnectTimer);
      pool.reconnectTimer = null;
    }
    console.error('[SSE] Token ใช้ไม่ได้แล้ว (401/403) — หยุด reconnect');
    emit('onAuthError');
    try {
      window.dispatchEvent(new CustomEvent('sse-auth-failed'));
    } catch (e) {}
  };

  /* sse_auth_stop_v1: ถาม status code ตรงๆ ว่าเป็นปัญหา token หรือปัญหาเน็ต
     fetch คืนค่าทันทีที่ได้ header (ไม่ต้องรออ่าน body ที่เป็น stream) แล้ว abort ทิ้ง */
  const probeAuth = async () => {
    if (pool.authFailed || pool.destroyed) return;
    if (pool.consecutiveFailures < AUTH_PROBE_AFTER_FAILURES) return;
    if (typeof fetch !== 'function' || typeof AbortController !== 'function') return;

    const now = Date.now();
    if (now - pool.lastProbeAt < AUTH_PROBE_MIN_INTERVAL) return;
    pool.lastProbeAt = now;

    const ctrl = new AbortController();
    const timer = setTimeout(() => ctrl.abort(), AUTH_PROBE_TIMEOUT);
    try {
      const res = await fetch(`${SSE_URL}?token=${encodeURIComponent(token)}`, {
        signal: ctrl.signal,
        headers: { Accept: 'text/event-stream' },
      });
      ctrl.abort(); // ได้ status แล้ว ไม่ต้องค้าง stream ไว้
      if (res.status === 401 || res.status === 403) stopForAuthFailure();
    } catch (e) {
      /* abort / เน็ตล่ม / timeout — สรุปไม่ได้ว่าเป็นปัญหา token ปล่อยให้ retry ต่อไป */
    } finally {
      clearTimeout(timer);
    }
  };

  const scheduleReconnect = () => {
    if (pool.destroyed || pool.authFailed) return;
    if (pool.reconnectTimer) clearTimeout(pool.reconnectTimer);

    console.log(`[SSE] Reconnect in ${pool.reconnectDelay}ms`);
    pool.reconnectTimer = setTimeout(() => {
      connect();
      /* sse_fast_reconnect_v1: gentler backoff 1.5x
         sse_auth_stop_v1: พลาดติดกันเยอะแล้ว (เช่นเซิร์ฟเวอร์ล่มยาว) ถ่างเพดานจาก 5 วิ เป็น 30 วิ
         — ช่วงแรกยังฟื้นเร็วเหมือนเดิม แต่ไม่ยิงรัวทั้งวันถ้าปลายทางไม่กลับมา */
      const cap = pool.consecutiveFailures >= SLOW_DOWN_AFTER_FAILURES
        ? LONG_MAX_RECONNECT_DELAY
        : MAX_RECONNECT_DELAY;
      pool.reconnectDelay = Math.min(Math.round(pool.reconnectDelay * 1.5), cap);
    }, pool.reconnectDelay);
  };

  /* sse_ticket_v1: ขอ "ตั๋ว" อายุสั้นใช้ครั้งเดียว เพื่อไม่ต้องแปะ JWT ไว้ใน URL
     (EventSource ใส่ header ไม่ได้ แต่ request ขอตั๋วเป็น fetch ธรรมดา ใส่ได้)
     คืนค่า: string = ตั๋ว | 'AUTH_FAILED' = token ตาย | 'UNSUPPORTED' = backend ยังไม่มี endpoint | null = ยิงไม่สำเร็จ */
  const requestTicket = async () => {
    if (typeof fetch !== 'function') return 'UNSUPPORTED';
    try {
      const res = await fetch(`${SSE_URL}/ticket`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 401 || res.status === 403) return 'AUTH_FAILED';
      if (res.status === 404 || res.status === 405) return 'UNSUPPORTED';
      if (!res.ok) return null;
      const data = await res.json();
      return (data && data.ticket) || null;
    } catch (e) {
      return null; // เน็ตมีปัญหา — ไม่ได้แปลว่า backend ไม่รองรับ
    }
  };

  const connect = async () => {
    if (pool.destroyed || pool.authFailed) return; /* sse_auth_stop_v1 */

    let url = null;
    if (!pool.ticketUnsupported) {
      const ticket = await requestTicket();
      if (pool.destroyed || pool.authFailed) return; // ระหว่างรอตั๋วอาจถูกปิดไปแล้ว

      if (ticket === 'AUTH_FAILED') {
        /* sse_ticket_v1: ได้คำตอบตรงๆ จาก status code — ไม่ต้องเดาเหมือนตอนใช้ EventSource */
        stopForAuthFailure();
        return;
      }
      if (ticket === 'UNSUPPORTED') {
        /* backend ยังไม่ได้อัปเดต — ถอยไปใช้ของเดิม จำไว้ไม่ต้องถามซ้ำทุกครั้ง
           มีไว้กันกรณี deploy หน้าเว็บก่อน backend เท่านั้น */
        console.warn('[SSE] ยังไม่มี endpoint ออกตั๋ว — ใช้ token ใน URL ไปก่อน');
        pool.ticketUnsupported = true;
      } else if (ticket) {
        url = `${SSE_URL}?ticket=${encodeURIComponent(ticket)}`;
      } else {
        /* ขอตั๋วไม่สำเร็จเพราะเน็ต — ลองใหม่รอบหน้า ไม่ถอยไปแปะ token ใน URL
           (ถ้าถอยทุกครั้งที่เน็ตสะดุด ก็ไม่ต่างจากเดิม) */
        pool.consecutiveFailures += 1;
        scheduleReconnect();
        return;
      }
    }
    if (!url) url = `${SSE_URL}?token=${encodeURIComponent(token)}`;

    console.log(`[SSE] Connecting... (${pool.subscribers.size} subscriber)`);

    try {
      pool.eventSource = new EventSource(url);
    } catch (e) {
      console.error('[SSE] Failed to create EventSource:', e);
      scheduleReconnect();
      return;
    }

    pool.eventSource.onopen = () => {
      console.log('[SSE] Connected ✅');
      pool.reconnectDelay = INITIAL_RECONNECT_DELAY;
      pool.consecutiveFailures = 0; /* sse_auth_stop_v1: ต่อติดแล้ว = เริ่มนับใหม่ */
      pool.connected = true; /* poll_adaptive_v1 */
      notifyStatus(token, true);
      /* realtime_recover_v1: ให้ทุก subscriber re-sync ตอน connect (รวม reconnect ตอน drop)
         หมายเหตุ sse_pool_v1: subscriber ที่ mount ทีหลังตอน connection เปิดค้างอยู่แล้ว
         จะไม่ได้ onConnect รอบนั้น — ต้องโหลดข้อมูลตั้งต้นเองตอน mount (ซึ่งทุกตัวทำอยู่แล้ว)
         onConnect มีไว้สำหรับ "กู้คืนหลังหลุด" เป็นหลัก

         conv_share_v1: แนบเวลาที่ต่อติดไปด้วย และคำนวณ "ครั้งเดียว" ตรงนี้
         ทุก subscriber จึงส่ง minTime ค่าเดียวกันเป๊ะ → เกาะ request เดียวกันได้
         ถ้าต่างคนต่างเรียก Date.now() เอง อาจต่างกัน 1ms แล้วกลายเป็นยิงคนละเส้น */
      emit('onConnect', { at: Date.now() });
    };

    Object.keys(EVENT_HANDLERS).forEach((evt) => {
      pool.eventSource.addEventListener(evt, (e) => {
        let data;
        try {
          data = JSON.parse(e.data);
        } catch (err) {
          console.error(`[SSE] ${evt} parse error:`, err);
          return;
        }
        emit(EVENT_HANDLERS[evt], data);
      });
    });

    pool.eventSource.onerror = () => {
      console.warn('[SSE] Error/disconnected');
      if (pool.eventSource) {
        pool.eventSource.close();
        pool.eventSource = null;
      }
      /* poll_adaptive_v1: หลุดแล้ว → บอกให้ฝั่ง polling กลับไปถี่เหมือนเดิม */
      if (pool.connected) {
        pool.connected = false;
        notifyStatus(token, false);
      }
      pool.consecutiveFailures += 1; /* sse_auth_stop_v1 */
      if (!pool.destroyed) {
        /* sse_ticket_v1: ถ้าใช้ตั๋วอยู่ ไม่ต้องเดาว่า token ตายไหม — การขอตั๋วรอบหน้า
           จะบอก status code ตรงๆ อยู่แล้ว ตัวถามนี้เหลือไว้ใช้เฉพาะเส้นทางเดิม */
        if (pool.ticketUnsupported) probeAuth();
        scheduleReconnect();
      }
    };
  };

  pool.start = connect;
  return pool;
}

function destroyPool(pool) {
  pool.destroyed = true;
  if (pool.connected) {
    pool.connected = false; /* poll_adaptive_v1 */
    notifyStatus(pool.token, false);
  }
  if (pool.reconnectTimer) {
    clearTimeout(pool.reconnectTimer);
    pool.reconnectTimer = null;
  }
  if (pool.eventSource) {
    pool.eventSource.close();
    pool.eventSource = null;
  }
  pools.delete(pool.token);
  console.log('[SSE] Closed (ไม่เหลือ subscriber แล้ว)');
}

export function connectSSE(handlers = {}) {
  const token = handlers.token || localStorage.getItem('token'); /* slip_upload_sse_v1: bill ส่ง bill_token, chat ใช้ token เดิม */
  if (!token) {
    console.warn('[SSE] No token, skip connect');
    return { close: () => {} };
  }

  let pool = pools.get(token);
  const isNewPool = !pool;
  if (isNewPool) {
    pool = createPool(token);
    pools.set(token, pool);
  }
  pool.subscribers.add(handlers);
  if (isNewPool) pool.start();

  let closed = false;
  return {
    close: () => {
      if (closed) return; // กัน close() ซ้ำไปลบ subscriber ของคนอื่น
      closed = true;
      pool.subscribers.delete(handlers);
      if (pool.subscribers.size === 0) destroyPool(pool);
    },
  };
}
